import { useState, type FormEvent } from "react";
import { useActiveTenant } from "../tenant/TenantContext";
import { useTenantMutation, useTenantQuery } from "../hooks/useTenantData";
import { can } from "../tenant/permissions";
import type { Block, BlockInput, Unit, UnitInput } from "../api/types";
import {
  Badge,
  Field,
  InlineError,
  PageHeader,
  Panel,
  QueryState,
} from "../components/ui";

export function StructurePage() {
  const membership = useActiveTenant();
  const canEdit = can(membership.role, "structure.edit");
  const blocks = useTenantQuery(["blocks"], (api, t) => api.listBlocks(t));
  const units = useTenantQuery(["units"], (api, t) => api.listUnits(t));

  const [blockFilter, setBlockFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const createBlock = useTenantMutation<BlockInput>(
    (api, t, input) => api.createBlock(t, input),
    [["blocks"], ["audit-events"]],
  );
  const toggleBlock = useTenantMutation<Block>(
    (api, t, block) => api.updateBlock(t, block.id, { active: !block.active }),
    [["blocks"], ["units"], ["audit-events"]],
  );
  const createUnit = useTenantMutation<UnitInput>(
    (api, t, input) => api.createUnit(t, input),
    [["units"], ["audit-events"]],
  );
  const toggleUnit = useTenantMutation<Unit>(
    (api, t, unit) => api.updateUnit(t, unit.id, { active: !unit.active }),
    [["units"], ["audit-events"]],
  );

  function submitBlock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    createBlock.mutate(
      { name: String(data.get("name") || ""), code: String(data.get("code") || "") || null },
      { onSuccess: () => form.reset() },
    );
  }

  function submitUnit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    createUnit.mutate(
      {
        block_id: String(data.get("block_id") || ""),
        label: String(data.get("label") || ""),
        floor: String(data.get("floor") || "") || null,
      },
      { onSuccess: () => form.reset() },
    );
  }

  return (
    <>
      <PageHeader title="Blocos e unidades" subtitle="Estrutura física do condomínio." />
      {canEdit && (
        <Panel title="Novo bloco ou torre">
          <form onSubmit={submitBlock} className="form-grid">
            <Field label="Nome" name="name" required placeholder="Torre A" />
            <Field label="Código (opcional)" name="code" placeholder="A" />
            <div className="form-actions">
              <button type="submit" disabled={createBlock.isPending}>
                {createBlock.isPending ? "Salvando…" : "Adicionar bloco"}
              </button>
            </div>
          </form>
          <InlineError error={createBlock.error} />
        </Panel>
      )}
      <Panel title="Blocos">
        <QueryState query={blocks} empty="Nenhum bloco cadastrado ainda.">
          {(items) => (
            <div className="table">
              {items.map((block) => (
                <div className="table-row" key={block.id}>
                  <span>
                    <strong>{block.name}</strong>
                    {block.code && <small>Código {block.code}</small>}
                  </span>
                  <Badge tone={block.active ? "ok" : "muted"}>{block.active ? "Ativo" : "Inativo"}</Badge>
                  {canEdit && (
                    <span className="row-actions">
                      <button
                        type="button"
                        className="secondary"
                        disabled={toggleBlock.isPending}
                        onClick={() => toggleBlock.mutate(block)}
                      >
                        {block.active ? "Inativar" : "Reativar"}
                      </button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </QueryState>
        <InlineError error={toggleBlock.error} />
      </Panel>
      {canEdit && (
        <Panel title="Nova unidade">
          <form onSubmit={submitUnit} className="form-grid">
            <label>
              Bloco
              <select name="block_id" required defaultValue="">
                <option value="" disabled>Selecione</option>
                {blocks.data?.filter((b) => b.active).map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </label>
            <Field label="Identificação" name="label" required placeholder="1203" />
            <Field label="Andar (opcional)" name="floor" placeholder="12" />
            <div className="form-actions">
              <button type="submit" disabled={createUnit.isPending}>
                {createUnit.isPending ? "Salvando…" : "Adicionar unidade"}
              </button>
            </div>
          </form>
          <InlineError error={createUnit.error} />
        </Panel>
      )}
      <Panel title="Unidades">
        <div className="toolbar">
          <label>
            Buscar
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Número da unidade"
            />
          </label>
          <label>
            Bloco
            <select value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)}>
              <option value="">Todos</option>
              {blocks.data?.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Mostrar inativas
          </label>
        </div>
        <QueryState query={units} empty="Nenhuma unidade cadastrada ainda.">
          {(items) => {
            const filtered = items.filter(
              (unit) =>
                (showInactive || unit.active) &&
                (!blockFilter || unit.block_id === blockFilter) &&
                (!search || unit.label.toLowerCase().includes(search.toLowerCase())),
            );
            if (filtered.length === 0) return <p className="state state-empty">Nenhuma unidade encontrada com os filtros atuais.</p>;
            return (
              <div className="table">
                {filtered.map((unit) => (
                  <div className="table-row" key={unit.id}>
                    <span>
                      <strong>Unidade {unit.label}</strong>
                      <small>
                        {unit.block_name}
                        {unit.floor ? ` · ${unit.floor}º andar` : ""}
                      </small>
                    </span>
                    <Badge tone={unit.active ? "ok" : "muted"}>{unit.active ? "Ativa" : "Inativa"}</Badge>
                    {canEdit && (
                      <span className="row-actions">
                        <button
                          type="button"
                          className="secondary"
                          disabled={toggleUnit.isPending}
                          onClick={() => toggleUnit.mutate(unit)}
                        >
                          {unit.active ? "Inativar" : "Reativar"}
                        </button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          }}
        </QueryState>
        <InlineError error={toggleUnit.error} />
      </Panel>
    </>
  );
}
